import type { SearchRequestArgs } from 'screens/Main/Search/searchState';
/**
 * Activity Feed Types
 * Matches backend DTOs from ActivityController
 */
import { SetDetail } from 'store/services/trade/tradeApi';

export interface ActivityUser {
  id: number;
  username: string;
  avatarUrl: string | null;
  countryCode: string | null;
  /** Raw country column as exposed by other endpoints (e.g. /public/profile returns
   *  `country: "US"`). Accepted as a fallback for `countryCode` so the avatar flag renders
   *  whichever name the backend activity DTO uses. See getActivityFeed transformResponse. */
  country?: string | null;
  points: number | null;
  isElite?: boolean;
  showAnimatedBorder?: boolean;
  borderColor1?: string | null;
  borderColor2?: string | null;
}

export interface ActivityCard {
  id: number;
  name: string;
  scryfallId: string;
  imageUrl: string; // Full CDN URL from backend - use directly
  setCode: string;
  collectorNumber: string | null;
  foil: boolean;
  /**
   * Search-tile fields (Asana 1219036410957400, backend contract 2026-09-30):
   * names and semantics identical to the /cards/search-light row for the same
   * printing. Always present on REST /activity/recent items; socket-pushed
   * items carry all of them EXCEPT holderCount (viewer-aware, one emit fans
   * out to every socket); /activity/available items carry none, hence optional.
   * An absent count leaves both menu rows enabled (strict === 0 disables).
   */
  setName?: string;
  setIconUrl?: string | null;
  printedName?: string | null;
  flavorName?: string | null;
  /** Scryfall verbatim; null = not backfilled (non-foil plus foil, never etched). */
  finishes?: string[] | null;
  promoTypes?: string[] | null;
  /** Per-finish points; null when unpriced, never 0. */
  nonFoilPoints?: number | null;
  foilPoints?: number | null;
  etchedPoints?: number | null;
  /** Distinct eligible users offering this printing (viewer-aware with a session). */
  holderCount?: number;
  /** Distinct eligible wanters of THIS printing who can afford it (viewer-independent). */
  wantedCount?: number;
  /**
   * Wanter acceptance criteria — what printing/finish/language/condition the requester will accept.
   * Present on want_added + available items only (the backend enriches these via the shared
   * acceptance module Trade Alerts uses). All optional → the flyout renders defensively
   * ("Any set / Any finish / Any language" when absent).
   */
  acceptableSetDetails?: SetDetail[];
  acceptableConditions?: string[];
  acceptableLanguages?: string[];
  acceptFoil?: boolean;
  acceptNonFoil?: boolean;
  availableInFoil?: boolean;
  availableInNonFoil?: boolean;
  availableLanguages?: Array<{ lang: string; scryfallId: string; setCode?: string }>;
}

export type ActivityType = 'trade_sent' | 'trade_received' | 'want_added' | 'card_added' | 'trade_shipped' | 'available';

export interface Activity {
  id: string; // Format: ${type}_${sourceId}_${itemIndex}
  type: ActivityType;
  createdAt: string; // ISO timestamp
  user: ActivityUser;
  card: ActivityCard;
  pointValue: number | null; // null → show "???"
  wantEntryId?: number; // Present on want_added and available items, used for dedup
  /**
   * The card is on the viewer's want list (matched by oracle id). Present only
   * when the request carried a session; absent for anonymous callers. The
   * backend still sends it; the frontend stopped reading it when the Home
   * "+ Want" pill was removed (Asana 1219036410957400), removable server-side.
   */
  matchesWantList?: boolean;
}

export interface RecentActivityResponse {
  activities: Activity[];
  nextCursor: string | null;
  hasMore: boolean;
  /** True when card filters were active and the 30-day lookback cap ended the walk. */
  lookbackCapped?: boolean;
}

export interface RecentActivityRequest {
  limit?: number;
  cursor?: string;
  /** Inclusive lower/upper bound on the acting trader's current point balance. */
  traderBalanceMin?: number;
  traderBalanceMax?: number;
  /** Card-attribute filters (Search vocabulary); serialized with appendCardFilterParams. */
  filters?: SearchRequestArgs;
}

/**
 * Activity Stats - Live Pulse Dashboard
 * Matches backend ActivityStatsResponse from GET /activity/stats
 */
export interface ActivityStats {
  /**
   * Non-cancelled trades initiated or completed in the rolling PULSE_TRADES_WINDOW_H
   * (24h) window. Replaced `tradesLastHour` (design pass 4, 2026-09-29); optional
   * until the backend ships the rename.
   */
  tradesLast24h?: number;
  /** COMPLETED trades, all time. The Pulse tile's fallback when the 24h count is 0. */
  tradesAllTime?: number;
  packagesShippedToday: number;
  cardsTradedThisWeek: number;
  /** Trade.status = TRANSIT, site-wide. Also the dots gate for the community map. */
  inTransitCount?: number;
  /** COMPLETED since UTC midnight. */
  packagesDeliveredToday?: number;
  /** Median TradeItem point value on trades touched in the last 7 days; 0 when there are none. */
  medianCardPointsThisWeek?: number;
  /** The viewer's own slice; null when anonymous (the homepage calls the auth-flagged sibling endpoint). */
  mine?: ActivityStatsMine | null;
}

export interface ActivityStatsMine {
  inTransit: number;
  arrivingToday: number;
  /** Recipient username of the viewer's soonest outbound package arriving today. */
  arrivingTodayTo: string | null;
  deliveredToday: number;
  /** Sender's coarse region of the most recent inbound delivery today (state or country, never a city). */
  deliveredTodayFromRegion: { state: string | null; country: string | null } | null;
  lastTradeAt: string | null;
  cardsTradedThisWeek: number;
}

/** GET /activity/map: route-aggregated community layers plus the viewer's own routes. */
export interface ActivityMapRegion {
  state: string | null;
  country: string | null;
}

export interface ActivityMapNode {
  /** Region key, the same format regionKey() produces (US-WA, DE). */
  key: string;
  region: ActivityMapRegion;
  /** Sum of counts on every route touching the key, including routes past the cap. */
  volume: number;
}

export interface ActivityMapRoute {
  from: string;
  to: string;
  count: number;
}

export interface ActivityMapMineTransit {
  tradeId: string;
  from: string;
  to: string;
  direction: 'outbound' | 'inbound';
  shippedAt: string;
  estimatedDeliveryBy: string | null;
  arrivesToday: boolean;
  /** The viewer's own trade partner, as /sending already shows them. */
  counterpartyUsername: string;
}

export interface ActivityMapMineDelivered {
  tradeId: string;
  from: string;
  to: string;
  direction: 'outbound' | 'inbound';
  completedAt: string;
}

export interface ActivityMapResponse {
  /** When the community block was computed; equal across calls inside the 60s cache. */
  generatedAt: string;
  countryCount: number;
  nodes: ActivityMapNode[];
  /** TRANSIT trades, top 500 by count. */
  inTransit: ActivityMapRoute[];
  /** COMPLETED in the last 24h, top 500 by count. */
  delivered: ActivityMapRoute[];
  unplaced: { inTransit: number; delivered: number };
  /** Null when anonymous, no shipping address, or no resolvable country. */
  me: { key: string; city: string | null; region: ActivityMapRegion } | null;
  mine: { inTransit: ActivityMapMineTransit[]; delivered: ActivityMapMineDelivered[] } | null;
}

/** GET /activity/heatmap?weeks=12 */
export interface HeatCounts {
  login: number;
  inboxMessage: number;
  haveAdd: number;
  wantAdd: number;
  sendPoints: number;
  tradeSuggest: number;
  tradeComplete: number;
  offerSent: number;
  offerResponded: number;
}

export interface HeatWeek {
  /** IYYY-Www, e.g. 2026-W39 */
  isoWeek: string;
  counts: HeatCounts;
}

export interface ActivityHeatmapResponse {
  joinedIsoWeek: string;
  currentIsoWeek: string;
  you: HeatWeek[];
  community: HeatWeek[];
}

/**
 * Get display text for activity type
 */
export const getActivityActionText = (type: ActivityType): string => {
  switch (type) {
    case 'trade_sent':
      return 'sent:';
    case 'trade_received':
      return 'received:';
    case 'want_added':
      return 'wants:';
    case 'card_added':
      return 'added:';
    case 'trade_shipped':
      return 'shipped:';
    case 'available':
      return 'available:';
    default:
      return '';
  }
};

/**
 * Available Feed Types
 * For GET /api/v1/activity/available endpoint
 */
export interface AvailableFeedRequest {
  limit?: number;
  hours?: number;
  /** Opaque keyset cursor for the next page (absent ⇒ first page). Backend owns page size. */
  cursor?: string;
  /** Inclusive lower/upper bound on the acting trader's current point balance. */
  traderBalanceMin?: number;
  traderBalanceMax?: number;
  filters?: SearchRequestArgs;
}

export interface AvailableFeedItem {
  wantEntryId: number;
  type: 'available';
  timestamp: string;
  card: {
    id: number;
    name: string;
    setName: string;
    setCode: string;
    collectorNumber: string;
    scryfallId: string;
    imageUrl: string;
    pointValue: number;
  };
  user: {
    id: number;
    username: string;
    avatarUrl: string | null;
    country: string | null;
    countryCode: string | null;
    points: number | null;
    isElite?: boolean;
    showAnimatedBorder?: boolean;
    borderColor1?: string | null;
    borderColor2?: string | null;
  };
}

export interface AvailableFeedResponse {
  items: AvailableFeedItem[];
  totalItems: number;
  lookbackHours: number;
  /** Opaque cursor for the next page, null at the end (mirrors RecentActivityResponse). */
  nextCursor: string | null;
  /** True when card filters were active and the 30-day lookback cap ended the walk. */
  lookbackCapped?: boolean;
  /** True iff another page of affordable wants exists past this one. */
  hasMore: boolean;
  /** First page only (no cursor): distinct traders and total points over the whole affordable predicate. */
  summary?: { traderCount: number; totalPoints: number };
}
